import React, { useState, useCallback, useEffect } from 'react'
import { Card, CardBody, Spinner, Badge, Button, Alert, Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui-compat'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import {
  faMapMarkerAlt,
  faExclamationTriangle,
  faImages,
  faCheckCircle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { useMutation } from '@apollo/react-hooks'
import OpenSeadragonViewer from './OpenSeadragonViewer'
import { PROCESS_BLUEPRINT_DZI } from '../../../../api/gql/mutation'
import { useJobProgress } from '../../../../hooks'
import { JobProgressIndicator } from '../../../../components/JobProgress'

interface BlueprintDzi {
  id: string
  dziUrl: string
  tilesBaseUrl: string
  width: number
  height: number
}

interface BlueprintJob {
  id: string
  type: string
  status: string
  progress: number
  currentStep?: string
  error?: string
}

interface BlueprintFile {
  id: string
  filename: string
  key: string
  size?: number
  blueprintDzi?: BlueprintDzi
  jobs?: BlueprintJob[]
}

interface Snag {
  id: string
  number: number
  displayNumber: string
  title: string
  status: string
  priority: string
  pinX: number | null
  pinY: number | null
  infrastructureType: string | null
}

interface BlueprintCanvasProps {
  blueprintFile: BlueprintFile
  snags: Snag[]
  selectedSnagId: string | null
  isPlacementMode: boolean
  onMarkerClick: (snagId: string) => void
  onPlaceMarker: (position: { x: number; y: number }) => void
  loading?: boolean
  isFullscreen?: boolean
  onOptimizeSuccess?: () => void
  focusPin?: { x: number; y: number } | null
}

const STATUS_LEGEND = [
  { status: 'OPEN', color: '#dc3545', key: 'open' },
  { status: 'IN_PROGRESS', color: '#ffc107', key: 'in_progress' },
  { status: 'RESOLVED', color: '#28a745', key: 'resolved' },
  { status: 'VERIFIED', color: '#17a2b8', key: 'verified' },
  { status: 'CLOSED', color: '#6c757d', key: 'closed' },
]

const BlueprintCanvas: React.FC<BlueprintCanvasProps> = ({
  blueprintFile,
  snags,
  selectedSnagId,
  isPlacementMode,
  onMarkerClick,
  onPlaceMarker,
  loading,
  isFullscreen = false,
  onOptimizeSuccess,
  focusPin,
}) => {
  const { t } = useTranslation()

  // Optimization modal state
  const [showOptimizeModal, setShowOptimizeModal] = useState(false)
  const [optimizeStatus, setOptimizeStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle')
  const [optimizeError, setOptimizeError] = useState<string | null>(null)

  // DZI processing mutation
  const [processDzi, { loading: processingDzi }] = useMutation(PROCESS_BLUEPRINT_DZI)

  // Job progress hook
  const { job, loading: jobLoading, startPolling, stopPolling } = useJobProgress({
    onComplete: () => {
      setOptimizeStatus('success')
      onOptimizeSuccess?.()
    },
    onError: (error) => {
      setOptimizeStatus('error')
      setOptimizeError(error)
    },
  })

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  // Get DZI data from blueprintDzi relation
  const { blueprintDzi } = blueprintFile
  const hasDzi = !!blueprintDzi

  // DZI URLs from the BlueprintDzi record
  const dziUrl = blueprintDzi?.dziUrl
  const tilesBaseUrl = blueprintDzi?.tilesBaseUrl
  const dziWidth = blueprintDzi?.width
  const dziHeight = blueprintDzi?.height

  // Check if there's an active DZI processing job
  const activeJob = blueprintFile.jobs?.find(
    (j) => j.type === 'DZI_PROCESSING' && (j.status === 'PENDING' || j.status === 'PROCESSING')
  )
  const failedJob = blueprintFile.jobs?.find(
    (j) => j.type === 'DZI_PROCESSING' && j.status === 'FAILED'
  )
  const isProcessingDzi = activeJob != null || processingDzi || jobLoading

  // Filter snags that have valid pin coordinates
  const pinnedSnags = snags.filter((snag) => snag.pinX !== null && snag.pinY !== null)

  // Handler for triggering DZI processing
  const handleProcessDzi = async () => {
    setShowOptimizeModal(true)
    setOptimizeStatus('processing')
    setOptimizeError(null)

    try {
      const { data } = await processDzi({
        variables: { fileId: blueprintFile.id },
      })

      const jobId = data?.processBlueprintDzi?.id
      if (jobId) {
        startPolling(jobId)
      } else {
        throw new Error('No job ID returned')
      }
    } catch (error: any) {
      console.error('Failed to start DZI processing:', error)
      setOptimizeStatus('error')
      setOptimizeError(error.message || t('Snags_optimize_unknown_error', 'Unknown error occurred'))
    }
  }

  // Close the optimization modal
  const handleCloseOptimizeModal = useCallback(() => {
    stopPolling()
    setShowOptimizeModal(false)
    setOptimizeStatus('idle')
    setOptimizeError(null)
  }, [stopPolling])

  // Auto-start polling if there's an active job on mount
  useEffect(() => {
    if (activeJob) {
      startPolling(activeJob.id)
    }
  }, [activeJob?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-trigger DZI processing for old blueprints that don't have DZI yet
  const [autoTriggered, setAutoTriggered] = useState(false)
  useEffect(() => {
    if (!hasDzi && !activeJob && !failedJob && !isProcessingDzi && !autoTriggered) {
      setAutoTriggered(true)
      handleProcessDzi()
    }
  }, [hasDzi, activeJob, failedJob, isProcessingDzi, autoTriggered]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset auto-trigger flag when blueprint changes
  useEffect(() => {
    setAutoTriggered(false)
  }, [blueprintFile.id])

  // If DZI tiles are available, use OpenSeadragon viewer
  if (hasDzi) {
    return (
      <Card
        className={`shadow-sm border-0 overflow-hidden ${isFullscreen ? 'mb-0' : ''}`}
        style={isFullscreen ? { flex: 1, display: 'flex', flexDirection: 'column' } : {}}
      >
        <CardBody className="p-0 relative" style={isFullscreen ? { flex: 1, display: 'flex', flexDirection: 'column' } : {}}>
          <OpenSeadragonViewer
            dziUrl={dziUrl!}
            dziTilesBaseUrl={tilesBaseUrl}
            dziWidth={dziWidth!}
            dziHeight={dziHeight!}
            snags={snags}
            selectedSnagId={selectedSnagId}
            isPlacementMode={isPlacementMode}
            onMarkerClick={onMarkerClick}
            onPlaceMarker={onPlaceMarker}
            isFullscreen={isFullscreen}
            focusPin={focusPin}
          />

          {/* Loading overlay */}
          {loading && (
            <div
              className="absolute flex items-center justify-center"
              style={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255,255,255,0.8)',
                zIndex: 1000,
              }}
            >
              <Spinner color="primary" />
            </div>
          )}
        </CardBody>

        {/* Compact Legend Footer */}
        <div className="card-footer bg-white border-t py-2 px-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-wrap" style={{ gap: '1rem' }}>
              <small className="text-muted-foreground font-bold mr-2">{t('Snags_legend')}:</small>
              {STATUS_LEGEND.map(({ status, color, key }) => (
                <span key={status} className="flex items-center">
                  <span
                    className="rounded-full inline-block mr-1"
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: color,
                      boxShadow: `0 0 0 2px ${color}33`,
                    }}
                  />
                  <small className="text-muted-foreground">{t(`Snags_status_${key}`)}</small>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
                <Icon icon={faImages as any} className="text-green-600" />
                {t('Snags_optimized_view', 'Optimized View')}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                <Icon icon={faMapMarkerAlt as any} />
                {pinnedSnags.length} / {snags.length} {t('Snags_pinned', 'pinned')}
              </span>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  // DZI not yet available — show processing state
  return (
    <>
      <Card className="shadow-sm border-0 overflow-hidden">
        <CardBody
          className="flex flex-col items-center justify-center text-center"
          style={{ height: '65vh', backgroundColor: '#f8f9fa' }}
        >
          {isProcessingDzi ? (
            <>
              <Spinner color="primary" className="mb-3" />
              <h5 className="text-muted-foreground mb-3">{t('Snags_optimizing', 'Optimizing blueprint...')}</h5>
              <div style={{ maxWidth: 400, width: '100%' }}>
                <JobProgressIndicator
                  job={job}
                  loading={jobLoading}
                  showSteps
                  showPercentage
                />
              </div>
              <p className="text-muted-foreground small mt-3 mb-0">
                {t('Snags_optimize_wait', 'Please wait while we process your blueprint for optimal viewing.')}
              </p>
            </>
          ) : failedJob ? (
            <>
              <Icon
                icon={faExclamationTriangle as any}
                className="text-[hsl(var(--warning))] mb-4"
                style={{ fontSize: '4rem', opacity: 0.5 }}
              />
              <h5 className="text-muted-foreground mb-3">{t('Snags_optimize_failed', 'Optimization Failed')}</h5>
              {failedJob.error && (
                <Alert color="warning" className="mb-3" style={{ maxWidth: 400 }}>
                  <small>{failedJob.error}</small>
                </Alert>
              )}
              <Button
                color="warning"
                onClick={handleProcessDzi}
                className="flex items-center"
              >
                <Icon icon={faImages as any} className="mr-2" />
                {t('Snags_retry_optimize', 'Retry optimization')}
              </Button>
            </>
          ) : (
            <>
              <Spinner color="primary" className="mb-3" />
              <h5 className="text-muted-foreground mb-3">{t('Snags_preparing_blueprint', 'Preparing blueprint...')}</h5>
              <p className="text-muted-foreground small mb-0">
                {t('Snags_optimize_auto_start', 'Blueprint optimization will start automatically.')}
              </p>
            </>
          )}
        </CardBody>
      </Card>

      {/* Optimization Modal */}
      <Modal isOpen={showOptimizeModal} toggle={optimizeStatus === 'processing' ? undefined : handleCloseOptimizeModal} centered>
        <ModalHeader toggle={optimizeStatus === 'processing' ? undefined : handleCloseOptimizeModal}>
          {t('Snags_optimize_title', 'Blueprint Optimization')}
        </ModalHeader>
        <ModalBody className="py-4">
          {optimizeStatus === 'processing' && (
            <>
              <JobProgressIndicator
                job={job}
                loading={jobLoading}
                showSteps
                showPercentage
              />
              <p className="text-muted-foreground small mt-3 mb-0 text-center">
                {t('Snags_optimize_wait', 'Please wait while we process your blueprint for optimal viewing.')}
              </p>
            </>
          )}

          {optimizeStatus === 'success' && (
            <div className="text-center">
              <Icon
                icon={faCheckCircle as any}
                className="text-[hsl(var(--success))] mb-3"
                style={{ fontSize: '3rem' }}
              />
              <h5 className="mb-2 text-[hsl(var(--success))]">{t('Snags_optimize_success', 'Optimization Complete!')}</h5>
              <p className="text-muted-foreground mb-0">
                {t('Snags_optimize_success_message', 'Your blueprint is now optimized for smooth pan & zoom viewing.')}
              </p>
            </div>
          )}

          {optimizeStatus === 'error' && (
            <div className="text-center">
              <Icon
                icon={faTimes as any}
                className="text-destructive mb-3"
                style={{ fontSize: '3rem' }}
              />
              <h5 className="mb-2 text-destructive">{t('Snags_optimize_failed', 'Optimization Failed')}</h5>
              <p className="text-muted-foreground mb-0">
                {optimizeError || t('Snags_optimize_error_message', 'An error occurred while optimizing the blueprint.')}
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {optimizeStatus === 'processing' ? (
            <Button color="secondary" disabled>
              <Spinner size="sm" className="mr-2" />
              {t('Snags_processing', 'Processing...')}
            </Button>
          ) : optimizeStatus === 'success' ? (
            <Button color="success" onClick={handleCloseOptimizeModal}>
              {t('Common_done', 'Done')}
            </Button>
          ) : (
            <>
              <Button color="secondary" onClick={handleCloseOptimizeModal}>
                {t('Common_close', 'Close')}
              </Button>
              <Button color="warning" onClick={handleProcessDzi}>
                {t('Snags_retry', 'Retry')}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  )
}

export default BlueprintCanvas
